/**
 * קטלוג המוצר — THE PLATFORM'S OWN CATALOGUE, AND THE SCREEN THE MASTER REQUIRED. X3 (R-2).
 *
 * Master §98 (M:725) lists *"Plans / SKUs / Module Pricing"* among the REQUIRED Platform navigation
 * items; §99 (M:728) requires the Platform Owner to be able to operate the commercial model with
 * zero customers. Until this address existed the Owner could fill the catalogue only with a
 * hand-typed INSERT, so `/platform/tenants/new` refused `catalogue_empty` on every fresh estate and
 * `PlatformTenantProvisioningScreen` could offer no next step at all.
 *
 * **The CTO's ruling in one line: a seed migration may not substitute for required UI. This is the
 * UI.** Four writes, one address, no price.
 *
 * ── TEMPLATE: COPIED, NOT INVENTED ──────────────────────────────────────────────────────────────
 *
 * The list + create + sub-write shape is `PlatformPriceBooksScreen` / `PlatformPriceBookScreen`:
 * `AppShell{titleHe, backToParent, provenanceHe}` → the outcome region → `ServerRead` → `DataTable`
 * or `StateBlock` → `signin__form` with `field`/`field__label`/`field__input` → `actions-row`.
 * Nothing here is a new component.
 *
 * ── ONE DELIBERATE DEVIATION FROM THAT TEMPLATE, AND IT IS THE POINT (UX B1) ─────────────────────
 *
 * Both price-book screens issue their READ ABOVE the surface guard (`PlatformPriceBooksScreen.tsx:40`
 * vs its guard at `:45`) — open finding F-ADD-1. Copied literally, this screen would issue three
 * PLATFORM reads on every wrong-surface and every unresolved arrival, and under X1 each refusal is an
 * audited row attributed to a principal who asked for nothing: false rows inside the record X1 exists
 * to make trustworthy. So it is built as the GUARD/BODY SPLIT of `PlatformTenantsScreen.tsx:43-73` —
 * the guard holds no hook that appends to the hook list, every read lives in the body, and
 * `coldLoad.dom.test.tsx` fails the day that is lost.
 *
 * ── WHY THE TWO SUB-WRITES ARE FORMS AND NOT ROW BUTTONS ────────────────────────────────────────
 *
 * The UX contract asked for them as row actions. A row action that has to carry a CHOICE — which
 * module, how many — needs either a modal or an inline-editable cell, and the same review lists both
 * on its must-not-invent list, as it does a multi-select. Its own RECOMMENDATION resolves it: *"one
 * code per submit, the price-book item-add shape"*. So each sub-write is a form under its grid,
 * taking a code, exactly as `PlatformPriceBookScreen`'s הוספת מחיר does. Declared, not silent.
 *
 * ── AND EVERY SELECTOR IS STILL IN THE SHELL (09 §3.4) ──────────────────────────────────────────
 *
 * This file owns no dropdown element at all — `shell/contextSingleSource.test.ts` scans the raw
 * source for one, comments included, so the forbidden tag is not even named here.
 *
 * ── AND THE SUGGESTION BOXES BECAME A PICKER. **The Owner's finding of 2026-10-20.** ───────────
 *
 * Seven choices here were `<input list="…">` with the code as the VALUE and the Hebrew name as the
 * option's text. That shape has three defects the Owner met in one sitting: the operator types and
 * the browser matches against the CODE, so the Hebrew name finds nothing; the popup's height and
 * spacing belong to the browser, which is why *"החלון הקיני קטן מדי לרשימה הארוכה"* could not be
 * fixed where it was written; and the name and the code are shown together always, which is the
 * *"כפילות קבועה באנגלית וקודים טכניים"* he asked to remove.
 *
 * `CataloguePicker` searches the HEBREW NAME, shows the code only as a muted secondary where two
 * rows could read alike, and submits through a hidden field — so the four handlers below still read
 * `FormData` by name. It is a `role="combobox"` over a `role="listbox"` list, and it is NOT a
 * native dropdown element: §3.4's rule is that the SHELL owns the context selectors, and this is a
 * form field inside a form. (The tag name is deliberately not written here -- the scan reads raw
 * source, comments included, and naming it would trip the check this paragraph is explaining.)
 *
 * WHAT IS LOST, STATED: an unlisted code can no longer be typed past the list. The old box let one
 * through and the server refused it from the far side; a choice that cannot name a thing the
 * catalogue does not have is the stricter contract, and the catalogue read failing now says so
 * instead of silently becoming a free-text box.
 *
 * ── WHAT THIS SCREEN DELIBERATELY CANNOT DO ─────────────────────────────────────────────────────
 *
 * No price, currency or amount (D-X3-1 — price lives in the price book, which has its own address).
 * No edit, no delete, no retire, no clone, no version, no effective date, no publish step, no bulk
 * import. And nothing customer-facing: the Master's own refusal copy forbids a customer plan list,
 * an upgrade button and a price (`api/denialText.ts:239-244`), and this address is PLATFORM-only.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { DataTable } from '../components/DataTable';
import { ServerRead } from '../components/ServerRead';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { PlanCatalogueEntry, SkuCatalogueEntry } from '../api/contract';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { CATALOGUE_STATUSES, catalogueStatusHe } from '../api/adminVocabulary';
import { CataloguePicker, type PickerOption } from '../components/CataloguePicker';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { organisationWording } from '../api/organisationWording';
import { routes } from '../routes';

const TITLE_HE = 'קטלוג התכניות והמוצרים';

/**
 * WHAT JUST HAPPENED, as one of five things. **UX B2.**
 *
 * The template renders NO success state: `PlatformPriceBookScreen.tsx:66-74` re-reads silently, and
 * the new row may land below the fold. On a screen with four writes that makes a saved plan
 * indistinguishable from a no-op — so an operator re-types the code, collides with `UQ_plan_code`,
 * and reads the collision as a broken screen.
 */
type Outcome =
  | { readonly kind: 'plan' }
  | { readonly kind: 'sku' }
  | { readonly kind: 'module' }
  | { readonly kind: 'inclusion' }
  | { readonly kind: 'quantity' }
  | { readonly kind: 'excluded'; readonly retained: number }
  /*
   * A FAILURE NAMES THE FORM IT FAILED IN. **Owner, 2026-10-23 §5.**
   *
   * It did not, and that is what kept every answer on this screen in one block above five forms:
   * a refusal that cannot say which write it belongs to can only be rendered somewhere general.
   * With the act named, each form prints its own result where its own button is — which is
   * *"הצג התקדמות ותוצאה היכן שהמשתמש מביט, גם בתחתית עמוד ארוך"*.
   */
  | { readonly kind: 'failed'; readonly what: Written; readonly failure: ActionFailure };

/** The five writes this screen performs, named. One of them is also an `Outcome` kind. */
type Written = 'plan' | 'sku' | 'module' | 'inclusion' | 'quantity' | 'excluded';

export function PlatformCatalogueScreen(): JSX.Element {
  const ctx = useWorkContext();

  /*
   * THE GUARD, AND NOT ONE HOOK BELOW IT. `useWorkContext` is a `useContext` read and appends nothing
   * to the hook list, so the unresolved render commits zero hooks and the body mounts cleanly when the
   * session lands. A single `useState` moved above this line turns the catalogue into a blank page.
   */
  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  return <PlatformCatalogueBody />;
}

function PlatformCatalogueBody(): JSX.Element {
  const readPlans = useCallback(() => callOperation('platform.catalogue.plan.read', {}), []);
  const readSkus = useCallback(() => callOperation('platform.catalogue.sku.read', {}), []);
  const readReference = useCallback(
    () => callOperation('platform.catalogue.reference.read', {}),
    [],
  );

  const plansRead = useServerData(readPlans);
  const skusRead = useServerData(readSkus);
  const referenceRead = useServerData(readReference);

  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /*
   * WHICH WRITE IS IN FLIGHT, not merely THAT one is. A boolean made all five buttons read
   * "שומר…" at once, so the screen answered "something is happening somewhere" to an operator who
   * had pressed one specific control. **Owner, 2026-10-23 §5.**
   */
  const [sending, setSending] = useState<Written | null>(null);

  /*
   * ── THE SEVEN CHOICES ON THIS SCREEN, EACH HELD AS STATE. **2026-10-20.** ──────────────────
   *
   * They were `<input list="…">` boxes, so the operator typed a CODE and the Hebrew name was
   * decoration the browser matched nothing against. `CataloguePicker` searches the Hebrew name and
   * submits the code through a hidden field of the same name, so the four handlers below still read
   * `FormData` exactly as they did.
   */
  /*
   * ── THE CODE IS DERIVED, NOT INVENTED. **Owner, section 2, 2026-11-01.** ───────────────────
   *
   *     *"קוד קטלוג נוצר אוטומטית; איני צריך להמציא אותו."*
   *
   * `billing.plan.plan_code` and `billing.product_sku.sku_code` are the identifiers every other
   * table joins on, and they are ASCII by constraint, so somebody entering a Hebrew product name
   * had to stop and compose a Latin identifier for it. That is not a commercial decision; it is
   * transliteration, and it is the kind of step that gets done inconsistently.
   *
   * The English name is the input, because the English name is already the Latin form of the
   * product — there is no second thing to type. The field stays EDITABLE: a code is permanent
   * once a subscription references it, and an operator who wants `CORE_PACK` rather than
   * `CORE_PACKAGE_2027` must be able to say so. `touched` is what stops the derivation from
   * overwriting a deliberate choice on the next keystroke in the name.
   */
  const [planCode, setPlanCode] = useState('');
  const [planCodeTouched, setPlanCodeTouched] = useState(false);
  const [skuCode, setSkuCode] = useState('');
  const [skuCodeTouched, setSkuCodeTouched] = useState(false);
  const [planStatus, setPlanStatus] = useState('active');
  const [skuStatus, setSkuStatus] = useState('active');
  const [chargeDimension, setChargeDimension] = useState('');
  /*
   * 0040. NO INITIAL VALUE, so the picker starts empty and `required` makes the operator choose.
   * Pre-selecting `volume` would be the default the column deliberately does not have.
   */
  const [tierMode, setTierMode] = useState('');
  const [includePlan, setIncludePlan] = useState('');
  const [includeSkuCode, setIncludeSkuCode] = useState('');
  const [attachSku, setAttachSku] = useState('');
  const [moduleToAttach, setModuleToAttach] = useState('');
  /*
   * THE COMPOSITION EDITOR'S OWN STATE. **Owner, 2026-10-21:** *"ודא שאפשר גם לערוך הרכב קיים
   * ולהוציא רכיב מחבילה"*.
   *
   * `composePlan` is which plan's members are open; `qtyDraft` holds the typed quantity per member,
   * keyed `planCode|skuCode`. A draft is NOT seeded from the server value: an empty draft means
   * "unchanged", so a stray focus cannot submit the number that was already there as if it were an
   * edit.
   */
  const [composePlan, setComposePlan] = useState('');
  const [qtyDraft, setQtyDraft] = useState<Readonly<Record<string, string>>>({});
  /*
   * ── WHICH ROW A PER-ROW WRITE BELONGS TO. **Measured in a browser, 2026-10-31.** ─────────────
   *
   * `Written` names the ACT. It cannot name the MEMBER, and the composition editor renders one
   * `feedbackFor('quantity')` and one `feedbackFor('excluded')` inside its row map — so a match on
   * the kind alone answered EVERY row at once. Saving a quantity on one member printed
   * "הכמות נשמרה · הרכב התכנית עודכן" under a member nobody had touched, and in the case that
   * found it, directly beneath that member's own sentence saying its impact could not be read and
   * *"אין לשנות את ההרכב"*. A row whose draft box was empty, and for which no request was made,
   * reported a save.
   *
   * This is the §5 finding one level down: `sending` was made per-ACT so one row's save would not
   * relabel the others' buttons, and the ANSWER was left shared. `null` is "this write has no row"
   * — the five forms above, each of which is the only renderer of its own kind.
   */
  const [writeScope, setWriteScope] = useState<string | null>(null);

  /**
   * ONE place a write is performed, so the outcome and the re-read cannot get out of step.
   *
   * `reload` runs only on success: a refused write changed nothing, and re-reading after one would
   * replace a stated refusal with a grid that looks fine.
   */
  function run(
    // THE WHOLE OUTCOME, not a kind. The `as Outcome` cast this used to need was what made a kind
    // carrying a figure -- `excluded` carries how many holders were RETAINED -- impossible to state.
    outcome: Exclude<Outcome, { readonly kind: 'failed' }>,
    act: () => Promise<unknown>,
    reload: () => void,
    // WHICH ROW, for the two writes that have one. Absent means the write has no row.
    scope?: string,
  ): void {
    // The act names itself from the outcome it intends, so a call site cannot describe one write
    // and report another — the two were already one argument, and this keeps them one.
    const what: Written = outcome.kind;
    setSending(what);
    setWriteScope(scope ?? null);
    setOutcome(null);
    void act()
      .then(
        () => {
          setOutcome(outcome);
          reload();
        },
        (error: unknown) => setOutcome({ kind: 'failed', what, failure: classifyActionError(error) }),
      )
      .finally(() => setSending(null));
  }

  /**
   * ── THE RESULT OF ONE WRITE, WHERE THAT WRITE'S BUTTON IS. **Owner, 2026-10-23 §5.** ─────────
   *
   * Five forms shared ONE outcome region at the top of the screen. The forms sit below four tables
   * and two of them are two screens down, so a save made at the bottom wrote its answer out of
   * sight — which an operator reads as a button that did nothing, and is the reading the Owner
   * reported. The copy below is unchanged; what changed is WHERE it is rendered.
   *
   * `role="alert"` is still at the call site rather than inside `StateBlock`: that component is a
   * plain section with no live region, and giving it one would change every screen in the product.
   * `role="status"` for the in-flight line, because "sending" is not an alert.
   */
  function feedbackFor(what: Written, scope?: string): JSX.Element | null {
    /*
     * THE ROW COMES FIRST, before the act and before the outcome — because the in-flight line is
     * rendered per row too, and a kind-only match made every member of the package read
     * "נשלח לשרת…" for a request sent about one of them.
     */
    if ((scope ?? null) !== writeScope) return null;
    if (sending === what) {
      return (
        <p className="action-state" data-state="sending" role="status">
          <span className="action-state__mark" aria-hidden="true" />
          נשלח לשרת…
        </p>
      );
    }
    if (outcome === null) return null;
    const belongs = outcome.kind === 'failed' ? outcome.what === what : outcome.kind === what;
    if (!belongs) return null;
    return (
      <div role="alert">
        {outcome === null ? null : outcome.kind === 'plan' ? (
          <StateBlock
            titleHe="התכנית נשמרה"
            bodyHe="התכנית נוספה לקטלוג. היא אינה מזכה בדבר עד שייכלל בה מוצר, ומוצר אינו פותח מודול עד שיצורף לו מודול."
          />
        ) : outcome.kind === 'sku' ? (
          <StateBlock
            titleHe="המוצר נשמר"
            bodyHe="המוצר נוסף לקטלוג. הוא אינו פותח שום מודול עד שיצורף לו מודול, ואינו נכלל בשום תכנית עד שייכלל בה."
          />
        ) : outcome.kind === 'module' ? (
          <StateBlock
            titleHe="המודול צורף למוצר"
            /*
             * THIS SAID *"מרגע זה כל לקוח שתכניתו כוללת את המוצר הזה זכאי למודול"* AND IT WAS NOT
             * TRUE. Measured on 2026-10-20: the entitlement resolver derives modules from the
             * SUBSCRIPTION's own lines (`billing.subscription_sku`), and reads `billing.plan_sku`
             * nowhere. Including a product in a plan granted nothing to anybody until the
             * subscription carried the line — which is the sentence below, and the reason the
             * subscription screen now has an action that applies a plan's composition.
             */
            bodyHe="הצירוף נשמר ונרשם. כל מנוי שנושא את המוצר הזה זכאי למודול — זכאות שהחבילה כוללת מוחלת על המנוי במסך ״מנוי ומכסות״, ומוצר שנמכר בנפרד נוסף כפריט."
          />
        ) : outcome.kind === 'inclusion' ? (
          <StateBlock
            titleHe="המוצר נכלל בתכנית"
            /*
             * AND THE SAME CORRECTION HERE. "התכנית מזכה כעת" described a derivation that does not
             * exist: a plan is a COMPOSITION, and a customer holds entitlement lines. The composition
             * reaches a customer when a subscription is opened on the plan or when it is applied to
             * an existing one, and saying so is what stops an operator concluding the work is done.
             */
            bodyHe="ההכללה נשמרה יחד עם הכמות. התכנית כוללת כעת את המוצר. לקוח קיים מקבל את הזכאות כשמחילים את הרכב התכנית על המנוי שלו, במסך ״מנוי ומכסות״ — כדי שזכויות של מנויים קיימים לא ישתנו בשקט."
          />
        ) : outcome.kind === 'quantity' ? (
          <StateBlock
            titleHe="הכמות נשמרה"
            bodyHe="הרכב התכנית עודכן. זכאות של מנוי קיים אינה משתנה מכך: הכמות החדשה חלה על מנוי חדש שייפתח על התכנית, ועל מנוי קיים רק כשמחילים עליו את ההרכב במסך ״מנוי ומכסות״."
          />
        ) : outcome.kind === 'excluded' ? (
          <StateBlock
            titleHe="הרכיב הוצא מהתכנית"
            /*
             * THE SENTENCE THE OPERATOR ACTUALLY NEEDS. Their question is not "did the row go" --
             * it is "what happened to the customers who have it". The answer is NOTHING, and the
             * figure is the one they acknowledged a moment earlier, so the message is specific
             * rather than reassuring in general.
             */
            bodyHe={
              outcome.retained === 0
                ? 'התכנית אינה כוללת עוד את הרכיב. אין מנויים חיים שנשאו אותו דרך התכנית הזאת, ולכן לא נשללה זכאות מאף לקוח.'
                : `התכנית אינה כוללת עוד את הרכיב. ${String(outcome.retained)} מנויים חיים ממשיכים להחזיק בו — ההוצאה מההרכב אינה שוללת זכאות שנרשמה כבר, והיא לא נשללה. שלילה מלקוח קיים היא פעולה נפרדת על המנוי שלו, והיא אינה נעשית מהמסך הזה.`
            }
          />
        ) : (
          <ActionFailureNotice
            failure={outcome.failure}
            /*
             * THE ONE CONFLICT THIS SCREEN KNOWS ABOUT, and it is the one an operator must act on.
             *
             * `impact_not_acknowledged` means the number of live holders MOVED between the read that
             * rendered it and the click that confirmed it -- somebody subscribed, or the screen had
             * been open a while. The server refuses rather than acting on a stale figure, and
             * returns the fresh one, so the honest response is to state the new number and ask
             * again. Anything else would be the silent change the requirement forbids.
             *
             * `impact_unknown` is the other half: the impact could not be MEASURED at all, so the
             * write refused. That is a 400 and falls through to the written generic, which is
             * correct -- it is not something the operator can resolve by confirming.
             */
            renderConflict={(conflict) =>
              conflict.body.error === 'impact_not_acknowledged' ? (
                <StateBlock
                  titleHe="ההרכב לא שונה — מספר המנויים התעדכן"
                  bodyHe={
                    conflict.body.liveSubscriptionCount === undefined
                      ? 'מספר המנויים החיים שמחזיקים ברכיב השתנה מאז קריאת המסך, והשרת סירב לשנות את ההרכב לפי מספר שאינו עדכני. לא נכתב דבר. רענון המסך יציג את המספר הנוכחי.'
                      : `מספר המנויים החיים שמחזיקים ברכיב הוא כעת ${String(conflict.body.liveSubscriptionCount)}, ולא המספר שהוצג כאן. השרת סירב לשנות את ההרכב לפי מספר שאינו עדכני, ולא נכתב דבר. רענון המסך יציג את המספר הנוכחי, ואז ניתן לאשר מחדש.`
                  }
                />
              ) : null
            }
          />
        )}
      </div>
    );
  }

  /**
   * AN ENGLISH NAME, AS A CATALOGUE CODE.
   *
   * Upper case, every run of anything that is not a Latin letter or a digit becomes one `_`, the
   * ends are trimmed, 32 characters at most — which is `CK_plan_code` and `CK_sku_code` exactly,
   * so a derived code can never be one the server refuses.
   *
   * A NAME WITH NO LATIN CHARACTERS DERIVES TO NOTHING, and that is the honest answer rather than
   * a transliteration this product is not qualified to invent. The field is then empty and
   * required, and the hint says the code may be typed.
   */
  function codeFrom(nameEn: string): string {
    return nameEn
      .toUpperCase()
      .replace(/[^A-Z0-9]+/gu, '_')
      .replace(/^_+|_+$/gu, '')
      .slice(0, 32);
  }

  function createPlan(event: FormEvent): void {
    event.preventDefault();
    const element = event.target as HTMLFormElement;
    const form = new FormData(element);
    run(
      { kind: 'plan' },
      () =>
        callOperation('platform.catalogue.plan.create', {
          body: {
            code: String(form.get('planCode') ?? ''),
            displayNameHe: String(form.get('planNameHe') ?? ''),
            displayNameEn: String(form.get('planNameEn') ?? ''),
            // SENT EXPLICITLY. Which status a new plan gets is the operator's statement, not a
            // default a form chose; the server accepts exactly `CK_plan_status` and nothing else.
            status: String(form.get('planStatus') ?? ''),
          },
        }),
      () => {
        /*
         * ── THE FORM EMPTIES ITSELF ON SUCCESS. **2026-11-01.** ────────────────────────────
         *
         * It did not, so after creating a plan the screen showed the create form still holding
         * the plan that had just been created. Pressing again is refused as a duplicate code,
         * which READS as a failure of the second attempt rather than as a success of the first —
         * and the derived code made it worse, because the code looked freshly generated.
         *
         * Reset is inside the RELOAD callback, which `run` calls only on the success arm: a
         * failed write keeps every value, which is the half that matters when somebody has typed
         * a long Hebrew name and the server refused the status.
         */
        element.reset();
        setPlanCode('');
        setPlanCodeTouched(false);
        plansRead.reload();
      },
    );
  }

  /**
   * SAVE A NEW QUANTITY FOR ONE MEMBER OF A PLAN.
   *
   * `acknowledgeLiveSubscriptions` is sent from the number THIS SCREEN RENDERED beside the row. If
   * it has moved since the read, the server refuses `409 impact_not_acknowledged` and returns the
   * current figure -- which `ActionFailureNotice` states, and a re-read then shows. That round trip
   * is the *"מבוקרת"* half: the operator confirms a number rather than being told one.
   */
  function saveQuantity(planCode: string, skuCode: string, liveHolders: number | null): void {
    const draft = qtyDraft[`${planCode}|${skuCode}`] ?? '';
    const quantity = Number.parseInt(draft, 10);
    run(
      { kind: 'quantity' },
      () =>
        callOperation('platform.catalogue.plan.sku.quantity', {
          params: { plan_code: planCode, sku_code: skuCode },
          body: {
            includedQuantity: quantity,
            // `null` means NOT READ. Sent as `-1` would be a lie; the server refuses
            // `impact_unknown` when it cannot measure, so an unreadable impact cannot be confirmed
            // here either -- the two halves agree rather than one of them guessing.
            acknowledgeLiveSubscriptions: liveHolders ?? -1,
          },
        }),
      plansRead.reload,
      // THE SAME KEY THE EDITOR BUILDS ITS ROWS WITH, so the answer lands under the row pressed.
      `${planCode}|${skuCode}`,
    );
  }

  /**
   * TAKE A MEMBER OUT OF A PLAN. It withdraws NOTHING from any live subscription.
   *
   * The confirmation text says so, because the operator's real question is *"what happens to the
   * customers who have it"* and the answer is "nothing, until you act on their subscription".
   */
  function excludeMember(planCode: string, skuCode: string, liveHolders: number | null): void {
    run(
      { kind: 'excluded', retained: liveHolders ?? 0 },
      () =>
        callOperation('platform.catalogue.plan.sku.exclude', {
          params: { plan_code: planCode, sku_code: skuCode },
          body: { acknowledgeLiveSubscriptions: liveHolders ?? -1 },
        }),
      plansRead.reload,
      `${planCode}|${skuCode}`,
    );
  }

  function createSku(event: FormEvent): void {
    event.preventDefault();
    const element = event.target as HTMLFormElement;
    const form = new FormData(element);
    run(
      { kind: 'sku' },
      () =>
        callOperation('platform.catalogue.sku.create', {
          body: {
            code: String(form.get('skuCode') ?? ''),
            displayNameHe: String(form.get('skuNameHe') ?? ''),
            displayNameEn: String(form.get('skuNameEn') ?? ''),
            chargeDimensionCode: String(form.get('chargeDimensionCode') ?? ''),
            // A CHECKBOX IS A BOOLEAN HERE AND NOWHERE ELSE. `is_sellable` is `BIT NOT NULL`; the
            // server refuses a non-boolean, so an unchecked box travels as `false` rather than absent.
            isSellable: form.get('isSellable') === 'on',
            status: String(form.get('skuStatus') ?? ''),
            /*
             * 0040. NO DEFAULT HERE EITHER. `tier_mode` is NOT NULL with no default, and a
             * form that quietly sent `volume` would decide *"המחיר חל על כל הכמות"* on the
             * operator's behalf — a hundred shekels on one office, taken by a field nobody saw.
             */
            tierMode: String(form.get('tierMode') ?? ''),
          },
        }),
      () => {
        // The same reset as `createPlan`, and for the same reason. Success arm only.
        element.reset();
        setSkuCode('');
        setSkuCodeTouched(false);
        setChargeDimension('');
        setTierMode('');
        skusRead.reload();
      },
    );
  }

  function attachModule(event: FormEvent): void {
    event.preventDefault();
    const form = new FormData(event.target as HTMLFormElement);
    run(
      { kind: 'module' },
      () =>
        callOperation('platform.catalogue.sku.module.attach', {
          params: { sku_code: String(form.get('attachSkuCode') ?? '') },
          body: { moduleCode: String(form.get('moduleCode') ?? '') },
        }),
      skusRead.reload,
    );
  }

  function includeSku(event: FormEvent): void {
    event.preventDefault();
    const form = new FormData(event.target as HTMLFormElement);
    run(
      { kind: 'inclusion' },
      () =>
        callOperation('platform.catalogue.plan.sku.include', {
          params: { plan_code: String(form.get('includePlanCode') ?? '') },
          body: {
            skuCode: String(form.get('includeSkuCode') ?? ''),
            includedQuantity: Number(form.get('includedQuantity') ?? ''),
          },
        }),
      plansRead.reload,
    );
  }

  const modules = referenceRead.result.state === 'loaded' ? referenceRead.result.data.modules : [];
  const dimensions =
    referenceRead.result.state === 'loaded' ? referenceRead.result.data.chargeDimensions : [];
  const planRows = plansRead.result.state === 'loaded' ? plansRead.result.data.plans : [];
  const skuRows = skusRead.result.state === 'loaded' ? skusRead.result.data.skus : [];

  /*
   * ── WHAT EACH PICKER OFFERS, AND IN WHOSE WORDS ───────────────────────────────────────────────
   *
   * Hebrew name first and alone; the CODE travels as `detail` only where an operator genuinely
   * needs to tell two rows apart, and never as a permanent second label — *"בלי כפילות קבועה
   * באנגלית וקודים טכניים"*. A row that cannot be chosen is SHOWN with the reason, because hiding
   * a retired plan leaves a customer sitting on one with no explanation.
   */
  const statusOptions: readonly PickerOption[] = CATALOGUE_STATUSES.map((status) => ({
    value: status,
    labelHe: catalogueStatusHe(status),
  }));

/*
 * ══ WHICH UNITS A TIER PRICE APPLIES TO — IN WORDS, NOT IN CODES. 0040. ═════════════════
 *
 * The Owner: *"ודא שהמנוע והממשק מגדירים אם המחיר חל על כל הכמות או רק על היחידות שבמדרגה."*
 * The labels are the arithmetic, so the choice can be made without reading a migration — and
 * `volume` on a Hebrew screen would say nothing at all.
 */
  const TIER_MODE_OPTIONS: readonly PickerOption[] = [
    { value: 'volume', labelHe: 'כל הכמות — המדרגה שהכמות נופלת בה קובעת את המחיר לכולן' },
    { value: 'graduated', labelHe: 'היחידות שבמדרגה בלבד — כל מדרגה מתמחרת את שלה' },
  ];

  const planOptions: readonly PickerOption[] = planRows.map((plan) => ({
    value: plan.planCode,
    labelHe: plan.displayNameHe,
    detail: plan.planCode,
    disabled: !plan.assignable,
    // OMITTED RATHER THAN `undefined`: `exactOptionalPropertyTypes` is on, and an optional prop
    // explicitly set to undefined is not the same as an absent one.
    ...(plan.assignable ? {} : { disabledReasonHe: catalogueStatusHe(plan.status) }),
  }));

  const skuOptions: readonly PickerOption[] = skuRows.map((sku) => ({
    value: sku.skuCode,
    labelHe: sku.displayNameHe,
    detail: sku.skuCode,
    // A RETIRED PRODUCT IS SHOWN AND NOT CHOOSABLE. `planned` IS choosable: a plan may be composed
    // before the product opens, and `applyPlanComposition` is what refuses to grant it early.
    disabled: sku.status === 'retired',
    ...(sku.status === 'retired' ? { disabledReasonHe: catalogueStatusHe(sku.status) } : {}),
  }));

  const moduleOptions: readonly PickerOption[] = modules.map((module) => ({
    value: module.moduleCode,
    labelHe: module.displayNameHe,
    // THE MODULE'S CODE IS NOT SHOWN. The Owner asked for Hebrew-only names in this picker by name;
    // the code is in the attached-modules list on the product row, where it is read and not chosen.
    disabled: module.status === 'retired',
    ...(module.status === 'retired' ? { disabledReasonHe: catalogueStatusHe(module.status) } : {}),
  }));

  const dimensionOptions: readonly PickerOption[] = dimensions.map((dimension) => ({
    value: dimension.dimensionCode,
    /*
     * THE SEEDED HEBREW from `billing.charge_dimension`, carried by the reference read since
     * 2026-10-20 rather than mapped again here -- PUT INTO THE PRODUCT'S CURRENT WORDING.
     *
     * One of these rows was seeded by `0005__reference_data.up.sql` as the retired noun. 0005 has
     * already run on the Owner's server, and he ruled out changing the schema or the identifiers
     * to carry a rename: *"this is a change to interface wording"*. So the row keeps its value and
     * the reader sees the product's word. This is the ONLY place a server-authored Hebrew label
     * still reaches a screen; see `api/organisationWording.ts` for why it is not applied widely.
     */
    labelHe: organisationWording(dimension.displayNameHe),
  }));

  /**
   * ── THE SEEDED HEBREW, FOR THE GRIDS AND NOT ONLY FOR THE PICKERS. **Owner, 2026-10-23 §6.** ──
   *
   * *"בדוק ערכים כמו active שנותרו באנגלית בטבלאות"* and *"בממשק עברית הצג שמות עסקיים בעברית"*.
   *
   * The two pickers above have read Hebrew names from `platform.catalogue.reference.read` since
   * 2026-10-20; the two GRIDS beside them went on printing `company` and a bare module code. One
   * read, two renderings, and only one of them in the operator's language.
   *
   * A code with no Hebrew is shown AS ITSELF rather than hidden or guessed at — the same rule
   * `catalogueStatusHe` states for a status this build does not know: the estate holds a value this
   * client has no name for, and the operator is shown the value their estate actually holds.
   */
  const dimensionNameHe = (code: string): string | null =>
    dimensions.find((row) => row.dimensionCode === code)?.displayNameHe ?? null;
  const moduleNameHe = (code: string): string | null =>
    modules.find((row) => row.moduleCode === code)?.displayNameHe ?? null;

  /**
   * WHAT INCLUDING A PRODUCT IN THIS PLAN REACHES, in one sentence.
   *
   * `null` from the server means the count was NOT READ, which is a different sentence from zero —
   * a screen that said "no customers are affected" because a port was unwired would be exactly the
   * silent change the chapter forbids.
   */
  function impactOf(planCode: string): string {
    const plan = planRows.find((row) => row.planCode === planCode);
    if (plan === undefined) return 'התכנית שנבחרה אינה ברשימה שנקראה, ולכן לא ניתן לומר כמה מנויים היא מתמחרת.';
    const count = plan.liveSubscriptionCount;
    if (count === null) {
      return 'מספר המנויים החיים בתכנית הזאת לא נקרא בהתקנה הזאת, ולכן אין כאן תצוגת השפעה. אין להסיק שאין מנויים.';
    }
    if (count === 0) {
      return 'אין מנויים חיים בתכנית הזאת, ולכן ההכללה אינה נוגעת באף לקוח קיים.';
    }
    return (
      `התכנית הזאת מתמחרת ${String(count)} מנויים חיים. ההכללה אינה משנה את זכאותם: ` +
      'מנוי קיים מקבל את הרכב התכנית רק כשמחילים אותו עליו במסך ״מנוי ומכסות״, לקוח־לקוח. ' +
      'מנוי חדש שייפתח על התכנית יקבל את ההרכב המעודכן.'
    );
  }

  const planColumns: readonly Column<PlanCatalogueEntry>[] = [
    {
      /*
       * THE SYSTEM-ISSUED NUMBER, FIRST. `0020`, Owner finding 7: *"נדרש מספר לקוח שהמערכת מקצה
       * ומציגה בכרטיס וברשימות"* -- and a plan and a product each get their own.
       *
       * `PLN-10001`, LTR, beside the name rather than instead of it, and the plan CODE stays where
       * it was. A row written before numbering says *"טרם הוקצה"* rather than rendering empty,
       * because an empty cell reads as a screen that failed to load the column.
       */
      key: 'number',
      headerHe: 'מספר',
      render: (plan) =>
        plan.displayNo === null ? (
          <span className="field__hint">טרם הוקצה</span>
        ) : (
          <span dir="ltr">{plan.displayNo}</span>
        ),
    },
    {
      /*
       * THE NAME, AND NOT THE NAME PLUS ITS CODE. **Owner, 2026-10-23 §6:** *"בממשק עברית הצג
       * שמות עסקיים בעברית בלי לשכפל קוד אנגלי לצד השם; קוד טכני יכול להופיע בפרטים."*
       *
       * The code has not been dropped — it is the column after this one, under its own heading,
       * which is what "in the details" means in a grid. What is gone is the permanent English
       * string pinned under every Hebrew name.
       */
      key: 'plan',
      headerHe: 'התכנית',
      render: (plan) => <span className="company-cell__name">{plan.displayNameHe}</span>,
    },
    {
      key: 'planCode',
      headerHe: 'קוד בקטלוג',
      render: (plan) => (
        <span className="company-cell__id" dir="ltr">
          {plan.planCode}
        </span>
      ),
    },
    {
      key: 'state',
      headerHe: 'מצב',
      render: (plan) =>
        plan.assignable ? (
          <StatusChip labelHe="ניתנת לשיוך" tone="ok" />
        ) : (
          <StatusChip labelHe="אינה ניתנת לשיוך" tone="muted" />
        ),
    },
    {
      // `active` / `planned` / `retired` printed as themselves, to a Hebrew reader, in a column
      // headed in Hebrew. The vocabulary to render them has been in this file's imports all along.
      key: 'status',
      headerHe: 'מחזור חיים',
      render: (plan) => <span>{catalogueStatusHe(plan.status)}</span>,
    },
    {
      /*
       * A REAL `0`. A plan with no SKU grants nothing — the resolver walks plan → SKU → module — so
       * this column is what separates "this plan is empty" from "the inclusions did not render".
       * NO TOTALS ROW: a sum of included quantities across plans is not a quantity of anything.
       */
      key: 'skus',
      headerHe: 'מוצרים בתכנית',
      numeric: true,
      render: (plan) => <span dir="ltr">{plan.skuCount}</span>,
    },
  ];

  const skuColumns: readonly Column<SkuCatalogueEntry>[] = [
    {
      /** `PRD-10001`. See the plan grid's first column for why it leads and why `null` says so. */
      key: 'number',
      headerHe: 'מספר',
      render: (sku) =>
        sku.displayNo === null ? (
          <span className="field__hint">טרם הוקצה</span>
        ) : (
          <span dir="ltr">{sku.displayNo}</span>
        ),
    },
    {
      /** The same rule as the plan grid's name column, and the code is the column after it. */
      key: 'sku',
      headerHe: 'המוצר',
      render: (sku) => <span className="company-cell__name">{sku.displayNameHe}</span>,
    },
    {
      key: 'skuCode',
      headerHe: 'קוד בקטלוג',
      render: (sku) => (
        <span className="company-cell__id" dir="ltr">
          {sku.skuCode}
        </span>
      ),
    },
    {
      key: 'dimension',
      headerHe: 'ממד החיוב',
      render: (sku) => {
        const he = dimensionNameHe(sku.chargeDimensionCode);
        // The seeded Hebrew when the reference read has it; the code itself when it does not,
        // because a dimension this client cannot name is still the one the estate is charging on.
        return he === null ? <span dir="ltr">{sku.chargeDimensionCode}</span> : <span>{he}</span>;
      },
    },
    {
      key: 'sellable',
      headerHe: 'נמכר',
      render: (sku) =>
        sku.sellable ? <StatusChip labelHe="נמכר" tone="ok" /> : <StatusChip labelHe="אינו נמכר" tone="muted" />,
    },
    {
      key: 'status',
      headerHe: 'מחזור חיים',
      render: (sku) => <span>{catalogueStatusHe(sku.status)}</span>,
    },
    {
      /*
       * ONE CHIP PER CODE, each `dir="ltr"`. A joined comma string would be one bidi run inside an
       * RTL paragraph and would render in an order nobody intended; the precedent is the blocker
       * chips at `PlatformSupportGrantsScreen.tsx:202-208`. Nothing is truncated — no truncation
       * pattern exists in this product.
       */
      key: 'modules',
      headerHe: 'מודולים שהמוצר מזכה בהם',
      render: (sku) =>
        sku.moduleCodes.length === 0 ? (
          <StatusChip labelHe="אין מודולים — המוצר אינו פותח דבר" tone="warn" />
        ) : (
          <span className="chip-row">
            {sku.moduleCodes.map((code) => {
              const he = moduleNameHe(code);
              // The module's seeded Hebrew name; the code only where this build has no name for it.
              return he === null ? (
                <span key={code} className="chip" dir="ltr">
                  {code}
                </span>
              ) : (
                <span key={code} className="chip">
                  {he}
                </span>
              );
            })}
          </span>
        ),
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'קונסולת הפלטפורמה', href: routes.platformConsole }}
    >
      {/*
        ── THE PUBLIC DOOR LIVES BESIDE THE CATALOGUE, AND IS REACHED FROM IT. §10ב. ──────────
        
        Not from the settings destination in the navigation: that entry carries
        `platform.subscription.edit`, which a Platform OPERATOR holds, and the door carries
        `platform.catalogue.plan.manage`, which only the Owner does. A link from a screen governed
        by the weaker capability would offer an operator a switch the gate will refuse — the thing
        `platform.policy.read`'s own comment refuses to do.

        It belongs here on its merits too: the second lock is a column on `billing.plan`, and
        "which plans may be bought without talking to us" is a catalogue decision.
      */}
      {/*
        ── THE WORK ORDER, AS FOUR PLACES TO GO. **Owner, section 2, 2026-11-01.** ──────────────

            *"סדר העבודה: מוצרים ← חבילות והרכבן ← מחירון ← פתיחה להרשמה."*

        **WHAT THIS REPLACES.** A four-item list that read `1 תכנית · 2 מוצר · 3 מוצר ← מודול — זה
        מה שהמערכת בודקת בכל בקשה · 4 תכנית ← מוצר, עם כמות`. Every word of it was true and none of
        it was a place to go: it described a dependency graph to somebody who wanted to know where
        to click next, it named the steps in the opposite order to the screen, and the third item
        explained the entitlement resolver in a strip above a form.

        *"להסיר מלל קבוע שאינו עוזר לבצע פעולה"* — so the strip is now the four steps the Owner
        named, each one a LINK: two to the sections of this screen, two to the screens that own
        the other halves. The two that are elsewhere carry `btn--nav`, which is the product's
        "this navigates" treatment, so a step that leaves this screen looks different from one
        that scrolls within it.

        THE PRICE BOOK AND THE PUBLIC DOOR WERE ALREADY REACHABLE and are not new capabilities —
        the door's link was a sentence above this strip and the price book had no link from here
        at all, which is why step 3 of the Owner's four was the one with no route to it.
      */}
      {/* A ROW AND NOT A COLUMN. The Owner's four steps read left-to-right as a sequence, and
          *"ניצול רוחב המסך"* is the same section's instruction. `.steps` is already a wrapping
          flex row; `--stacked` is the MFA enrolment screen's variant, where three long sentences
          genuinely are a column. */}
      <ol className="steps" aria-label="סדר העבודה בקטלוג">
        <li className="steps__item" data-state="current">
          <span className="steps__index" dir="ltr">
            1
          </span>
          <a className="steps__label" href="#catalogue-skus">
            מוצרים
          </a>
        </li>
        <li className="steps__item" data-state="current">
          <span className="steps__index" dir="ltr">
            2
          </span>
          <a className="steps__label" href="#catalogue-plans">
            חבילות והרכבן
          </a>
        </li>
        <li className="steps__item" data-state="current">
          <span className="steps__index" dir="ltr">
            3
          </span>
          <a className="steps__label btn btn--nav" href={routes.platformPriceBooks}>
            מחירון
          </a>
        </li>
        <li className="steps__item" data-state="current">
          <span className="steps__index" dir="ltr">
            4
          </span>
          <a className="steps__label btn btn--nav" href={routes.platformSignupDoor}>
            פתיחה להרשמה
          </a>
        </li>
      </ol>

      {/*
        ── THE ORDER OF THIS SCREEN IS THE ORDER OF THE WORK. **Owner, section 2, 2026-11-01.** ──

            *"סדר העבודה: מוצרים ← חבילות והרכבן ← מחירון ← פתיחה להרשמה."*

        Products came SECOND and plans first, which is the dependency order written backwards: a
        plan is empty until a product is included in it, and a product grants nothing until a
        module is attached to it. Somebody following the screen downwards met the plan form before
        anything existed to put in a plan, and the four-step strip above had to explain in words
        what the layout was contradicting.

        **AND THE COMPOSITION EDITOR MOVED WITH IT.** *"חבילות והרכבן"* is one step, and the editor
        for an existing plan's members was nested at the bottom of the PRODUCTS section — three
        screens' worth of scrolling away from the plan table it edits. It is now the last thing in
        the plans section, which is where somebody goes to change a plan.

        Nothing in either block changed; the two were cut and placed in the Owner's order.
      */}
      {/* ── THE SKUs ────────────────────────────────────────────────────────────────────────── */}

      <section id="catalogue-skus">
        <h2 className="breakdown__title">מוצרים</h2>

        <ServerRead result={skusRead.result} whatHe="את המוצרים" skeletonRows={4}>
          {(data) =>
            data.skus.length === 0 ? (
              <StateBlock
                titleHe="עדיין אין מוצרים בקטלוג"
                bodyHe="הקריאה לשרת הצליחה ואין מוצרים. זהו המצב הצפוי לפני שנוצר מוצר ראשון, ובלעדיו תכנית אינה מזכה בשום מודול. הצעד הבא הוא יצירת מוצר בטופס שלמטה וצירוף מודול אליו."
                tone="empty"
              />
            ) : (
              <DataTable<SkuCatalogueEntry>
                captionHe="מוצרים"
                columns={skuColumns}
                rows={data.skus}
                rowKey={(sku) => sku.skuCode}
                filterSummaryHe={`מציג ${data.skus.length} מוצרים, כולל מוצרים שאינם נמכרים`}
                emptyStateHe="אין מוצרים בקטלוג. הקריאה לשרת הצליחה."
              />
            )
          }
        </ServerRead>

        <form className="form" onSubmit={createSku}>
          <h2 className="form__title">מוצר חדש</h2>
          <div className="form__group form__group--wide">
            <label className="field">
              <span className="field__label">שם בעברית</span>
              <input className="field__input" name="skuNameHe" maxLength={128} required />
            </label>
            <label className="field">
              <span className="field__label">שם באנגלית</span>
              <input
                className="field__input"
                name="skuNameEn"
                dir="ltr"
                maxLength={128}
                onChange={(e) => {
                  if (!skuCodeTouched) setSkuCode(codeFrom(e.currentTarget.value));
                }}
                required
              />
            </label>
            <label className="field">
              <span className="field__label">קוד בקטלוג — נוצר אוטומטית מהשם באנגלית</span>
              <input
                className="field__input"
                name="skuCode"
                dir="ltr"
                maxLength={32}
                value={skuCode}
                onChange={(e) => {
                  setSkuCodeTouched(true);
                  setSkuCode(e.currentTarget.value);
                }}
                required
              />
              <span className="field__hint">ניתן לשנות. הקוד קבוע לאחר שמנוי מפנה אליו.</span>
            </label>
            <CataloguePicker
              name="chargeDimensionCode"
              labelHe="ממד החיוב"
              options={dimensionOptions}
              value={chargeDimension}
              onChange={setChargeDimension}
              required
              hintHe="השמות מגיעים מקטלוג ממדי החיוב עצמו, ולא מרשימה שנכתבה כאן."
            />
            <CataloguePicker
              name="skuStatus"
              labelHe="מצב המוצר"
              options={statusOptions}
              value={skuStatus}
              onChange={setSkuStatus}
              required
            />
            <CataloguePicker
              name="tierMode"
              labelHe="המחיר במדרגות חל על"
              options={TIER_MODE_OPTIONS}
              value={tierMode}
              onChange={setTierMode}
              required
              hintHe="למוצר במחיר אחיד הבחירה אינה משנה. היא מכריעה רק כשיש יותר ממדרגה אחת — ואז ההפרש הוא מאות שקלים על ארגון אחד."
            />
            <label className="field">
              <span className="field__label">נמכר ללקוחות</span>
              <input className="field__input" name="isSellable" type="checkbox" />
            </label>
            {/* THE SAVE, AND ITS ANSWER, INSIDE THE GROUP IT BELONGS TO. The label no longer
                changes to "שומר…" — the progress is its own line beside the button, so the control
                an operator aimed at keeps the words they aimed at. */}
            <div className="actions-row">
              <button type="submit" className="btn btn--primary" disabled={sending !== null}>
                יצירת מוצר
              </button>
              {feedbackFor('sku')}
            </div>
          </div>
        </form>

        <form className="form" onSubmit={attachModule}>
          <h2 className="form__title">צירוף מודול למוצר</h2>
          <div className="form__group form__group--wide">
            <CataloguePicker
              name="attachSkuCode"
              labelHe="המוצר"
              options={skuOptions}
              value={attachSku}
              onChange={setAttachSku}
              required
            />
            <CataloguePicker
              name="moduleCode"
              labelHe="המודול"
              options={moduleOptions}
              value={moduleToAttach}
              onChange={setModuleToAttach}
              required
              emptyHe="קטלוג המודולים ריק. אין מה לצרף עד שיוגדר מודול."
              hintHe="שמות המודולים בעברית. הקוד הטכני אינו נדרש כאן."
            />
            {/* THE SAVE, AND ITS ANSWER, INSIDE THE GROUP IT BELONGS TO. The label no longer
                changes to "שומר…" — the progress is its own line beside the button, so the control
                an operator aimed at keeps the words they aimed at. */}
            <div className="actions-row">
              <button type="submit" className="btn btn--primary" disabled={sending !== null}>
                שמירת שיוך המודול
              </button>
              {feedbackFor('module')}
            </div>
          </div>
        </form>
      </section>

      {/* ── THE PLANS ───────────────────────────────────────────────────────────────────────── */}

      <section id="catalogue-plans">
        <h2 className="breakdown__title">חבילות והרכבן</h2>

        <ServerRead result={plansRead.result} whatHe="את התכניות" skeletonRows={4}>
          {(data) =>
            data.plans.length === 0 ? (
              /*
               * A LEGITIMATE EMPTY, AND THE CLAUSE THAT SAYS SO IS VERBATIM. `הקריאה לשרת הצליחה` is
               * what separates "there are none" from "the request was refused", and it is the same
               * sentence `PlatformPriceBooksScreen.tsx:120` and `PlatformHealthScreen.tsx:152` use.
               */
              <StateBlock
                titleHe="עדיין אין תכניות בקטלוג"
                bodyHe="הקריאה לשרת הצליחה ואין תכניות. זהו המצב הצפוי לפני שנוצרה תכנית ראשונה, וכל עוד הוא נמשך לא ניתן לפתוח מנוי לאף לקוח. כדי להתחיל: יצירת תכנית בטופס שלמטה, יצירת מוצר, צירוף מודול למוצר, והכללת המוצר בתכנית עם כמות — בסדר הזה."
                tone="empty"
              />
            ) : (
              <DataTable<PlanCatalogueEntry>
                captionHe="תכניות"
                columns={planColumns}
                rows={data.plans}
                rowKey={(plan) => plan.planCode}
                filterSummaryHe={`מציג ${data.plans.length} תכניות, כולל תכניות שאינן ניתנות לשיוך`}
                emptyStateHe="אין תכניות בקטלוג. הקריאה לשרת הצליחה."
              />
            )
          }
        </ServerRead>

        <form className="form" onSubmit={createPlan}>
          <h2 className="form__title">תכנית חדשה</h2>
          <div className="form__group form__group--wide">
            <label className="field">
              <span className="field__label">שם בעברית</span>
              <input className="field__input" name="planNameHe" maxLength={128} required />
            </label>
            <label className="field">
              <span className="field__label">שם באנגלית</span>
              <input
                className="field__input"
                name="planNameEn"
                dir="ltr"
                maxLength={128}
                onChange={(e) => {
                  if (!planCodeTouched) setPlanCode(codeFrom(e.currentTarget.value));
                }}
                required
              />
            </label>
            {/* THE CODE, WRITTEN BY THE ENGLISH NAME AND STILL THE OPERATOR'S TO CHANGE. */}
            <label className="field">
              <span className="field__label">קוד בקטלוג — נוצר אוטומטית מהשם באנגלית</span>
              <input
                className="field__input"
                name="planCode"
                dir="ltr"
                maxLength={32}
                value={planCode}
                onChange={(e) => {
                  setPlanCodeTouched(true);
                  setPlanCode(e.currentTarget.value);
                }}
                required
              />
              <span className="field__hint">ניתן לשנות. הקוד קבוע לאחר שמנוי מפנה אליו.</span>
            </label>
            <CataloguePicker
              name="planStatus"
              labelHe="מצב התכנית"
              options={statusOptions}
              value={planStatus}
              onChange={setPlanStatus}
              required
              hintHe="רק תכנית במצב ״פעיל״ ניתנת לשיוך למנוי."
            />
            {/* THE SAVE, AND ITS ANSWER, INSIDE THE GROUP IT BELONGS TO. The label no longer
                changes to "שומר…" — the progress is its own line beside the button, so the control
                an operator aimed at keeps the words they aimed at. */}
            <div className="actions-row">
              <button type="submit" className="btn btn--primary" disabled={sending !== null}>
                יצירת תכנית
              </button>
              {feedbackFor('plan')}
            </div>
          </div>
        </form>

        <form className="form" onSubmit={includeSku}>
          <h2 className="form__title">הכללת מוצר בתכנית</h2>
          <div className="form__group form__group--wide">
            <CataloguePicker
              name="includePlanCode"
              labelHe="התכנית"
              options={planOptions}
              value={includePlan}
              onChange={setIncludePlan}
              required
              emptyHe="אין עדיין תכניות. יצירת תכנית בטופס שמעל היא הצעד הראשון."
            />
            <CataloguePicker
              name="includeSkuCode"
              labelHe="המוצר"
              options={skuOptions}
              value={includeSkuCode}
              onChange={setIncludeSkuCode}
              required
              emptyHe="אין עדיין מוצרים. יצירת מוצר וצירוף מודול אליו הם הצעדים שלפני ההכללה."
            />
            <label className="field">
              <span className="field__label">כמות יחידות כלולה</span>
              {/*
                WHAT THE NUMBER COUNTS, AND WHAT IT DOES NOT. **Owner, 2026-10-26:** *"a module is
                an entitlement: there or not. It may not be bought twice and may not show a
                quantity."* It does not: entitlement is set membership in
                `entitlement-resolver.ts`, which never reads this number, and zero here still
                licenses the SKU's modules. The number is UNITS OF CHARGE -- 08 gives each SKU its
                own unit -- and saying so is the fix, because an unlabelled 5 reads as five copies.
              */}
              <input
                className="field__input"
                name="includedQuantity"
                type="number"
                /*
                 * `min={0}`, WHICH IS `CK_plan_sku_qty` EXACTLY. The constraint is `>= 0` and zero is a
                 * real inclusion — the plan licenses the SKU's modules and includes no units of it. A
                 * `min={1}` here would refuse in the browser a value the database accepts, which is a
                 * commercial rule invented in a form.
                 */
                min={0}
                step={1}
                dir="ltr"
                required
              />
            </label>
            {/*
              A SAVE THAT LOOKS LIKE A SAVE. **The Owner's finding of 2026-10-20:** *"״הכללת המוצר
              בתכנית״ ו״צירוף המודול״ נראו כטקסט רגיל ולא ככפתורי שמירה"*. The cause was
              `btn--quiet`, whose rule is `background: none; border-color: transparent;
              text-decoration: underline` — a link, by construction. A primary button and a busy
              label are what §9 asks for: a prominent action and a visible state while it runs.
            */}
            {/* ── THE IMPACT, BEFORE THE SAVE. **The commercial chapter, 2026-10-20.** ──────
                *"שינוי חבילה לא ישנה זכויות של מנויים קיימים בשקט. נדרשים הצגת השפעה וקביעת
                תחולה"*. So the number of customers this plan prices is shown here, with what will
                and will not happen to them — and the second half is the load-bearing one: since
                this round, a plan's composition reaches an existing subscription only when it is
                applied to that subscription explicitly. */}
            {includePlan === '' ? null : (
              <p className="tile__lead" data-tone="warn">
                {impactOf(includePlan)}
              </p>
            )}
            {/* THE SAVE, AND ITS ANSWER, INSIDE THE GROUP IT BELONGS TO. The label no longer
                changes to "שומר…" — the progress is its own line beside the button, so the control
                an operator aimed at keeps the words they aimed at. */}
            <div className="actions-row">
              <button type="submit" className="btn btn--primary" disabled={sending !== null}>
                שמירת הכללת המוצר בתכנית
              </button>
              {feedbackFor('inclusion')}
            </div>
          </div>
        </form>
        {/*
          ══ 5 · עריכת הרכב חבילה קיימת, והוצאת רכיב ממנה ═════════════════════════════════════════

          **The Owner's requirement of 2026-10-21:** *"ודא שאפשר גם לערוך הרכב קיים ולהוציא רכיב
          מחבילה, ולא רק ליצור ולהוסיף. ההשפעה על מנויים קיימים חייבת להיות מפורשת ומבוקרת."*

          Until this section the catalogue could only GROW: four forms that create and add, and no
          way to change or remove anything. The composition was not even VISIBLE — the grid above
          says how many products a plan includes and never which.

          THE IMPACT SITS BESIDE EVERY ROW, and it is the number the write demands back. A row whose
          count could not be read says so and cannot be edited at all, because the server refuses
          `impact_unknown` rather than letting an unmeasured change through.

          NOT A FORM. Each row acts on itself: one member is one decision, and a single submit over a
          list of them would make "which of these did I just change" unanswerable.
        */}
        {/*
          `signin__form` WAS THE SIGN-IN CARD'S NARROW COLUMN, borrowed here as it was on the two
          price-book screens. **Owner section 2, 2026-11-01:** *"טפסים קומפקטיים בשורות או
          בקוביות סמוכות, עם ניצול רוחב המסך."* This is a picker and a list of members; it uses
          the product's own form layout, which flows and collapses on its own.
        */}
        <div className="form">
          <div className="form__group form__group--wide">
            <h3 className="form__group-title">עריכת הרכב חבילה</h3>
            <p className="tile__lead">
              בחירת חבילה מציגה את הרכיבים שבה. לכל רכיב ניתן לשנות כמות או להוציא אותו מההרכב.
              הוצאת רכיב אינה שוללת זכאות שנרשמה כבר אצל לקוח קיים — היא קובעת מה החבילה תעניק מכאן
              והלאה.
            </p>
            <CataloguePicker
              name="composePlanCode"
              labelHe="החבילה"
              options={planOptions}
              value={composePlan}
              onChange={setComposePlan}
              emptyHe="אין עדיין תכניות, ולכן אין הרכב לערוך."
              hintHe="בחירת חבילה מציגה את רכיביה מתחת."
            />

            {composePlan === '' ? null : <CompositionEditor
              plan={planRows.find((row) => row.planCode === composePlan)}
              qtyDraft={qtyDraft}
              setQtyDraft={setQtyDraft}
              sending={sending}
              feedbackFor={feedbackFor}
              onSaveQuantity={saveQuantity}
              onExclude={excludeMember}
            />}
          </div>
        </div>
      </section>
    </AppShell>
  );
}

/**
 * ONE PLAN'S MEMBERS, EACH WITH ITS OWN IMPACT AND ITS OWN TWO ACTIONS.
 *
 * A component of its own rather than an inline IIFE inside the screen: the body needs four branches
 * (plan absent from the read, plan empty, a member whose impact is unreadable, a member that can be
 * edited) and an IIFE with four branches inside JSX is where the fourth stops being reviewable.
 *
 * `plan` is `undefined` when the picked code is not in the answer the server gave. That is not an
 * empty composition and is not rendered as one.
 */
function CompositionEditor({
  plan,
  qtyDraft,
  setQtyDraft,
  sending,
  feedbackFor,
  onSaveQuantity,
  onExclude,
}: {
  readonly plan: PlanCatalogueEntry | undefined;
  readonly qtyDraft: Readonly<Record<string, string>>;
  readonly setQtyDraft: (next: (previous: Readonly<Record<string, string>>) => Readonly<Record<string, string>>) => void;
  /** WHICH write is in flight, so one row's save does not relabel the others. `null` is idle. */
  readonly sending: Written | null;
  /**
   * The screen's own renderer for "what happened to this write", handed down rather than
   * duplicated: the two actions in each row are the two whose results were furthest from them —
   * this editor sits below four tables and five forms. **Owner, 2026-10-23 §5.**
   *
   * THE SECOND ARGUMENT IS NOT OPTIONAL HERE IN PRACTICE, and the type says so as loudly as a type
   * can: every call below passes the row key, because this is the one caller whose renderer runs
   * more than once on one screen. It is declared optional only because the five forms above share
   * the same function and have no row.
   */
  readonly feedbackFor: (what: Written, scope?: string) => JSX.Element | null;
  readonly onSaveQuantity: (planCode: string, skuCode: string, liveHolders: number | null) => void;
  readonly onExclude: (planCode: string, skuCode: string, liveHolders: number | null) => void;
}): JSX.Element {
  if (plan === undefined) {
    return (
      <StateBlock
        titleHe="החבילה אינה ברשימה שנקראה"
        bodyHe="לא ניתן להציג הרכב של חבילה שאינה בתשובת השרת. רענון המסך יקרא את הקטלוג מחדש."
      />
    );
  }
  /*
   * ── A COMPOSITION THAT WAS NOT READ IS NOT AN EMPTY PACKAGE ─────────────────────────────────
   *
   * `includedSkus` is REQUIRED by `PlanCatalogueEntry`, and this component read `.length` off it
   * with no guard — so a server that did not send it (an older API behind the same address) threw
   * during render, and the whole catalogue screen became `RenderFaultBoundary`'s generic fault.
   * Found on 2026-10-23 by opening this editor in a browser, which no case in the suite does.
   *
   * `?? []` WOULD HAVE BEEN WORSE THAN THE CRASH. It renders "החבילה ריקה", and that sentence has
   * a meaning an operator acts on: a customer subscribed to this package is entitled to no module
   * through it. Saying that about a list nobody read is the family of defect this screen's own
   * `liveHolders === null` branch exists to avoid — *"אין להסיק שאין מנויים"*. So absence is its
   * own third state, and it says which of the two it is.
   */
  if (plan.includedSkus === undefined) {
    return (
      <StateBlock
        titleHe="הרכב החבילה לא נקרא"
        bodyHe="השרת לא החזיר את רכיבי החבילה הזאת, ולכן אין כאן הרכב להציג. אין להסיק מכך שהחבילה ריקה — חבילה ריקה היא תשובה אחרת, והיא נאמרת במילים אחרות."
        tone="unavailable"
        nextStep={<p>רענון המסך יקרא את הקטלוג מחדש.</p>}
      />
    );
  }
  if (plan.includedSkus.length === 0) {
    return (
      <StateBlock
        titleHe="החבילה ריקה"
        bodyHe="החבילה הזאת אינה כוללת אף מוצר, ולכן לקוח שמנויו עליה אינו זכאי לשום מודול דרכה. הוספת מוצר נעשית בטופס ״הכללת מוצר בתכנית״ שמעל."
      />
    );
  }

  return (
    <ul className="breakdown">
      {plan.includedSkus.map((member) => {
        const key = `${plan.planCode}|${member.skuCode}`;
        const draft = qtyDraft[key] ?? '';
        /*
         * `null` IS NOT ZERO. An unreadable impact disables both actions, because the server refuses
         * `impact_unknown` for exactly this state — so offering the buttons would offer a refusal.
         */
        const unreadable = member.liveHolders === null;
        const typed = Number.parseInt(draft, 10);
        const savable =
          sending === null &&
          !unreadable &&
          draft.trim() !== '' &&
          Number.isInteger(typed) &&
          typed >= 0 &&
          typed !== member.includedQuantity;

        return (
          <li className="breakdown__row" key={key}>
            <span className="company-cell">
              <span className="company-cell__name">{member.displayNameHe}</span>
              <span className="company-cell__id" dir="ltr">
                {member.skuCode}
              </span>
            </span>

            {/*
              THE PRESENT QUANTITY, STATED, so the input beside it is an edit and not a guess.

              `<p>`, NOT `<span>`, AND THAT IS THE WHOLE OF THE FIX. **Measured, 2026-10-31.**
              Both notes were inline spans with no separator between them, and neither class has a
              rule in `styles.css` — so the browser set them on one line touching: the last glyph of
              this note ended at x=951.4 and the first glyph of the next began at x=951.4, a gap of
              0.00px on the same baseline. A package member included ONCE, held by THREE live
              subscriptions, rendered as *"כמות כלולה כיום: 13 מנויים חיים…"*. The figure an
              operator is about to overwrite was displayed as a different number.

              jsdom could not see it and cannot: it applies no layout. What it CAN see is that these
              are block boxes, which is why the element was changed rather than the stylesheet.
            */}
            <p className="breakdown__note">
              {`כמות כלולה כיום: ${String(member.includedQuantity)}`}
              {/* The same Hebrew the two grids above use. An `active` printed here to a Hebrew
                  reader is the §6 finding in the one place it was left. */}
              {member.skuStatus === 'active' ? '' : ` · מצב המוצר: ${catalogueStatusHe(member.skuStatus)}`}
            </p>

            <p className="breakdown__note">
              {unreadable
                ? 'מספר המנויים החיים שמחזיקים ברכיב הזה לא נקרא בהתקנה הזאת. לכן אין כאן תצוגת השפעה ואין לשנות את ההרכב — אין להסיק שאין מנויים.'
                : member.liveHolders === 0
                  ? 'אין מנויים חיים שמחזיקים ברכיב הזה דרך החבילה. שינוי או הוצאה אינם נוגעים בלקוח קיים.'
                  : `${String(member.liveHolders ?? 0)} מנויים חיים מחזיקים ברכיב הזה. שינוי ההרכב אינו משנה את זכאותם, והוצאה אינה שוללת אותה.`}
            </p>

            <label className="field">
              <span className="field__label">כמות חדשה</span>
              <input
                className="field__input"
                type="number"
                min={0}
                step={1}
                dir="ltr"
                inputMode="numeric"
                value={draft}
                disabled={sending !== null || unreadable}
                onChange={(event) => {
                  const next = event.target.value;
                  setQtyDraft((previous) => ({ ...previous, [key]: next }));
                }}
              />
            </label>

            <span className="actions-row">
              <button
                type="button"
                className="btn btn--primary"
                disabled={!savable}
                onClick={() => onSaveQuantity(plan.planCode, member.skuCode, member.liveHolders)}
              >
                שמירת הכמות
              </button>
              {feedbackFor('quantity', key)}
              {/*
                THE SECONDARY TREATMENT, AND THE CHOICE IS DELIBERATE. Four variants exist and three
                of them would say something untrue here:

                  `btn--primary`      would compete with the save beside it -- two primaries in one
                                      row is the confusion finding 9 is about.
                  `btn--quiet`        is `background: none; border-color: transparent; underline` --
                                      a LINK by construction, which is the other half of finding 9:
                                      *"נראו כטקסט רגיל ולא ככפתורי שמירה"*.
                  `btn--irreversible` is the filled negative treatment, and this is NOT
                                      irreversible: the component can be included again, and no
                                      entitlement is withdrawn from anyone. Red here would overstate
                                      the act, which is its own kind of dishonesty.

                A bare `.btn` is a bordered button on a sunken fill -- visibly an action, clearly
                not the primary one. It is already used exactly this way elsewhere.
              */}
              <button
                type="button"
                className="btn"
                disabled={sending !== null || unreadable}
                onClick={() => onExclude(plan.planCode, member.skuCode, member.liveHolders)}
              >
                הוצאה מההרכב
              </button>
              {feedbackFor('excluded', key)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
